// Modules
import { isEmpty } from 'lodash';
import React from 'react';
import { CellProps } from 'react-table';

// Components
import AnglerExpanded from '../../components/AnglerExpanded/AnglerExpanded';
import Message from '../../components/Message/Message';
import ReactTable from '../../components/ReactTable/ReactTable';

// Config
import { CLASS_PREFIX, DEFAULT_STRING_NO_TABLE_RESULTS } from '../../config';

// Types
import {
  AdvertisementVariant,
  ICatchesTableData,
  IRowSeparator,
} from '../../types';

// Util
import { basstrakkCatchesTop5 } from '../../util/processing';
import { classList } from '../../util/util';

// Hooks
import useWindowDimensions from '../../hooks/useWindowDimensions';

// Table columns
import { TabProvider } from '../../components/Context';
import RowSeparator from '../../components/TableRow/RowSeparator';
import { BigBassColumns } from './BigBassColumns';

interface IBigBassTable {
  data: any[];
  tabLabel: string;
  tabClass: string;
}

const BigBassTable = (props: IBigBassTable) => {
  const { data } = props;

  const { width } = useWindowDimensions();

  const renderRow = ({ row, index }: CellProps<ICatchesTableData>) => {
    // When a row is expanded to show the Angler's information we want to
    // change the color of the row.
    const expandedRowColor = row.isExpanded
      ? {
          style: {
            background: '#f9ba5e',
          },
        }
      : {};

    const stripingClass = index % 2 ? 'even' : 'odd';

    const key = `bigbass-${row.index}-${row.values.catchTime}`;

    return (
      <div
        // We can use the getToggleRowExpandedProps prop-getter
        // to build the toggle for the expanded row
        {...row.getToggleRowExpandedProps(expandedRowColor)}
        key={key}
        className={classList(
          `${CLASS_PREFIX}row row-index-${row.index} index-${index}`
        )}
      >
        {row.cells.map(cell => {
          return (
            <div
              {...cell.getCellProps()}
              className={classList(
                (cell.column as any).className,
                stripingClass
              )}
            >
              {cell.render('Cell', { index, width })}
            </div>
          );
        })}
      </div>
    );
  };

  // Allows rendering an "expanded" row below a default table row.
  const renderExpanded = ({ row, index }: CellProps<ICatchesTableData>) => (
    <AnglerExpanded
      anglerId={row.original.Angler.FISHERMENID}
      anglerName={row.original.Angler.FISHERMAN_PHONENAME} // Must have anglerName now to retrieve videos from BrightCove.
      catchData={basstrakkCatchesTop5(
        row.original.Angler.FISHERMENID,
        row.original.Catches
      )}
      count={index}
    />
  );

  const renderRowSeparator = ({ numRows, index }: IRowSeparator) => (
    <RowSeparator
      numRows={numRows}
      index={index}
      variant={AdvertisementVariant.BIGBASS}
    />
  );

  // We have a default message when there is no data
  const shouldShow = isEmpty(data);

  // for (let t = 1; t <= 102; t++) {
  //   data.push({ AngerId: 0 });
  // }

  return (
    <>
      {shouldShow ? (
        <Message
          message={DEFAULT_STRING_NO_TABLE_RESULTS}
          customClass="no-results-message"
          shouldShow={shouldShow}
        />
      ) : (
        <>
          <TabProvider value={{ tab: 'bigbass' }}>
            <ReactTable
              tableName="bigbass"
              columns={BigBassColumns}
              data={data}
              initialState={{
                sortBy: [
                  {
                    id: 'fishWeight',
                    desc: true,
                  },
                  {
                    id: 'catchTime',
                    desc: false,
                  },
                ],
              }}
              renderRow={renderRow}
              renderExpanded={renderExpanded}
              renderRowSeparator={renderRowSeparator}
              classPrefix={CLASS_PREFIX}
            />
          </TabProvider>
        </>
      )}
    </>
  );
};

export default BigBassTable;
